import { useState } from "react";
import { Card, Text, Anchor, Group, Badge } from "@mantine/core";
import { IconMapPin } from "@tabler/icons-react";
import { sosApi } from "../../api/sos";
import type { SOSAlert } from "../../types/domain";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable, type Column } from "../../components/common/DataTable";
import { TripLink } from "../../components/common/TripLink";
import {
  ListDateRangeFilter,
  type DateRange,
} from "../../components/common/ListDateRangeFilter";
import { ExportMenu } from "../../components/common/ExportMenu";
import { collectAll, type ExportField } from "../../lib/export";
import {
  DetailDrawer,
  DetailField,
  DetailSection,
} from "../../components/common/DetailDrawer";
import { humanize } from "../../lib/labels";
import { formatDateTime } from "../../lib/format";
import { errorMessage } from "../../lib/axios";

const sosExportFields: ExportField<SOSAlert>[] = [
  { label: "Raised", value: (s) => s.occurredAt ?? s.createdAt },
  { label: "Raised by", value: (s) => s.occurredBy },
  { label: "Name", value: (s) => s.name },
  { label: "Phone", value: (s) => s.phoneNumber },
  {
    label: "Trip",
    value: (s) => (typeof s.tripID === "string" ? s.tripID : s.tripID?._id),
  },
  { label: "Latitude", value: (s) => s.location?.latitude },
  { label: "Longitude", value: (s) => s.location?.longitude },
];

function mapsHref(loc?: { latitude?: number; longitude?: number }) {
  if (loc?.latitude == null || loc?.longitude == null) return null;
  return `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`;
}

export function SosPage() {
  const [selected, setSelected] = useState<SOSAlert | null>(null);

  const [range, setRange] = useState<DateRange>({});
  const filters = { startDate: range.startDate, endDate: range.endDate };
  const { page, setPage, result, isLoading, error } = usePaginatedList<SOSAlert>({
    queryKey: ["sos"],
    fetcher: sosApi.list,
    filters,
  });

  const columns: Column<SOSAlert>[] = [
    {
      key: "time",
      header: "Raised",
      render: (s) => (
        <Text size="sm">{formatDateTime(s.occurredAt ?? s.createdAt)}</Text>
      ),
    },
    {
      key: "who",
      header: "Raised by",
      render: (s) => (
        <Group gap={6} wrap="nowrap">
          <Badge size="sm" variant="light" color={s.occurredBy === "DRIVER" ? "steel" : "oxblood"}>
            {humanize(s.occurredBy)}
          </Badge>
          <Text size="sm">{s.name ?? "—"}</Text>
        </Group>
      ),
    },
    { key: "phone", header: "Phone", render: (s) => s.phoneNumber ?? "—" },
    { key: "trip", header: "Trip", render: (s) => <TripLink trip={s.tripID} /> },
    {
      key: "loc",
      header: "Location",
      render: (s) => {
        const href = mapsHref(s.location);
        return href ? (
          <Anchor
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            onClick={(e) => e.stopPropagation()}
          >
            <Group gap={4} wrap="nowrap">
              <IconMapPin size={14} /> Map
            </Group>
          </Anchor>
        ) : (
          "—"
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="SOS Alerts" subtitle="Emergency alerts raised by riders & drivers" />
      <Card withBorder radius="md" padding="lg">
        <Group mb="md" gap="sm">
          <ListDateRangeFilter
            value={range}
            onChange={(r) => {
              setRange(r);
              setPage(1);
            }}
          />
          <ExportMenu
            filename="sos-alerts"
            fields={sosExportFields}
            fetchAll={() => collectAll(sosApi.list, { ...filters })}
          />
        </Group>
        <DataTable
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(s) => s._id}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel="No SOS alerts"
          onRowClick={setSelected}
          page={page}
          totalPages={result?.totalPages}
          total={result?.total}
          onPageChange={setPage}
        />
      </Card>

      <DetailDrawer
        opened={!!selected}
        onClose={() => setSelected(null)}
        title="SOS Alert"
      >
        {selected && (
          <>
            <DetailSection title="Alert">
              <DetailField label="Raised by" value={humanize(selected.occurredBy)} />
              <DetailField label="Name" value={selected.name} />
              <DetailField label="Phone" value={selected.phoneNumber} />
              <DetailField
                label="Raised at"
                value={formatDateTime(selected.occurredAt ?? selected.createdAt)}
              />
            </DetailSection>
            <DetailSection title="Context">
              <DetailField label="Trip" value={<TripLink trip={selected.tripID} />} />
              <DetailField
                label="Location"
                value={
                  selected.location?.latitude != null
                    ? `${selected.location.latitude}, ${selected.location.longitude}`
                    : "—"
                }
              />
              {mapsHref(selected.location) && (
                <DetailField
                  label=""
                  value={
                    <Anchor href={mapsHref(selected.location)!} target="_blank" rel="noopener noreferrer">
                      Open in Google Maps
                    </Anchor>
                  }
                />
              )}
            </DetailSection>
          </>
        )}
      </DetailDrawer>
    </>
  );
}
